/**
 * AuditLogFilters Component
 *
 * Provides filtering controls for audit logs
 * Supports date range, entity type, operation type, and user filtering
 *
 * Related Story: WORKFORCE-V2-story-2.004
 */

import React, { useState } from 'react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { X, Filter } from 'lucide-react'
import { format } from 'date-fns'
import type { AuditLogQueryParams } from '@/lib/services/audit-log-api'

interface AuditLogFiltersProps {
  filters: AuditLogQueryParams
  onFilterChange: (filters: AuditLogQueryParams) => void
}

export function AuditLogFilters({ filters, onFilterChange }: AuditLogFiltersProps) {
  const [localFilters, setLocalFilters] = useState<AuditLogQueryParams>(filters)

  const entityTypes = ['EMPLOYEE', 'DEPARTMENT', 'ROLE', 'PAYROLL']

  /**
   * Handle filter field changes
   */
  const handleFieldChange = (field: keyof AuditLogQueryParams, value: any) => {
    const updated = { ...localFilters, [field]: value }
    setLocalFilters(updated)
  }

  /**
   * Apply filters
   */
  const handleApply = () => {
    onFilterChange(localFilters)
  }

  /**
   * Clear all filters
   */
  const handleClear = () => {
    const cleared: AuditLogQueryParams = { limit: 100 }
    setLocalFilters(cleared)
    onFilterChange(cleared)
  }

  /**
   * Count active filters
   */
  const activeFilterCount = Object.keys(localFilters).filter(
    key => key !== 'limit' && key !== 'nextToken' && localFilters[key as keyof AuditLogQueryParams]
  ).length

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-brand-primary" />
            <h3 className="font-display text-lg font-semibold text-white">Filtros</h3>
            {activeFilterCount > 0 && (
              <Badge className="bg-brand-primary/20 text-brand-primary border-brand-primary/40">
                {activeFilterCount} filtro{activeFilterCount !== 1 ? 's' : ''} ativo{activeFilterCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="gap-2"
          >
            <X className="w-4 h-4" />
            Limpar Filtros
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range - Start Date */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Data Inicial
            </label>
            <input
              type="date"
              value={localFilters.startDate ? localFilters.startDate.split('T')[0] : ''}
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value).toISOString() : undefined
                handleFieldChange('startDate', date)
              }}
              className="w-full px-3 py-2 bg-brand-dark border border-brand-gray/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          {/* Date Range - End Date */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Data Final
            </label>
            <input
              type="date"
              value={localFilters.endDate ? localFilters.endDate.split('T')[0] : ''}
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value).toISOString() : undefined
                handleFieldChange('endDate', date)
              }}
              className="w-full px-3 py-2 bg-brand-dark border border-brand-gray/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          {/* Entity Type Filter */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Tipo de Entidade
            </label>
            <select
              value={localFilters.entityType || ''}
              onChange={(e) => handleFieldChange('entityType', e.target.value || undefined)}
              className="w-full px-3 py-2 bg-brand-dark border border-brand-gray/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Todos</option>
              {entityTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* User ID Filter */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              ID do Usuário
            </label>
            <input
              type="text"
              placeholder="Digite o ID do usuário..."
              value={localFilters.userId || ''}
              onChange={(e) => handleFieldChange('userId', e.target.value || undefined)}
              className="w-full px-3 py-2 bg-brand-dark border border-brand-gray/20 rounded-lg text-white placeholder-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
        </div>

        {/* Apply Button */}
        <div className="mt-4 flex justify-end">
          <Button
            variant="default"
            onClick={handleApply}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            Aplicar Filtros
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
